import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import { logout } from '@/api/auth-api'
import { restoreSession } from '@/api/client'
import { session } from '@/api/session'
import type { AuthenticatedUser, LoginOutcome } from '@/api/types'
import type { PermissionCode } from '@/lib/permissions'

import { AuthContext, type AuthContextValue, type AuthStatus, type PendingChallenge } from './auth-context'

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<AuthStatus>('initialising')
  const [user, setUser] = useState<AuthenticatedUser | null>(null)
  const [challenge, setChallenge] = useState<PendingChallenge | null>(null)

  // The access token is memory-only, so a reload always starts with nothing. The HttpOnly refresh
  // cookie is what carries the session across it: ask for a new access token once at start-up,
  // and fall back to the sign-in screen when the cookie is gone or refused.
  useEffect(() => {
    let cancelled = false

    session.restoreBranchId()

    void restoreSession().then((restored) => {
      if (cancelled) {
        return
      }

      if (restored) {
        setUser(restored.user)
        setStatus('authenticated')
      } else {
        setStatus('anonymous')
      }
    })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(
    () =>
      session.onExpired(() => {
        setUser(null)
        setChallenge(null)
        setStatus('anonymous')
        // Cached rows belong to the session that fetched them; a different user must not inherit
        // another's branch data from memory.
        queryClient.clear()
      }),
    [queryClient],
  )

  const applyOutcome = useCallback((outcome: LoginOutcome, username: string) => {
    switch (outcome.outcome) {
      case 'authenticated':
        session.setAccessToken(outcome.accessToken)
        setUser(outcome.user)
        setChallenge(null)
        setStatus('authenticated')
        break
      case 'mfa_required':
        setChallenge({ kind: 'totp', token: outcome.challengeToken, username })
        break
      case 'mfa_enrolment_required':
        setChallenge({
          kind: 'mfa_enrolment',
          token: outcome.challengeToken,
          username,
          reason: outcome.reason,
        })
        break
      case 'password_change_required':
        setChallenge({
          kind: 'password_change',
          token: outcome.challengeToken,
          username,
          reason: outcome.reason,
        })
        break
      default:
        // Exhaustiveness: an outcome this switch does not know must break the build rather
        // than leave the operator on a screen that does nothing.
        outcome satisfies never
    }

    return outcome
  }, [])

  const signOut = useCallback(async () => {
    await logout()
    setUser(null)
    setChallenge(null)
    setStatus('anonymous')
    queryClient.clear()
  }, [queryClient])

  const permissionSet = useMemo(() => new Set<string>(user?.permissions ?? []), [user])

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      challenge,
      isAuthenticated: status === 'authenticated' && user !== null,
      applyOutcome,
      clearChallenge: () => setChallenge(null),
      signOut,
      hasPermission: (permission: PermissionCode) => permissionSet.has(permission),
      hasAnyPermission: (permissions: readonly PermissionCode[]) =>
        permissions.some((permission) => permissionSet.has(permission)),
    }),
    [status, user, challenge, applyOutcome, signOut, permissionSet],
  )

  return <AuthContext value={value}>{children}</AuthContext>
}
